import i18nFactory, { i18nRaw as i18nRawFactory } from '@yandex-int/i18n';
import * as React from 'react';

import { StyledLink, StyledLinkTheme } from '../../../components/StyledLink/StyledLink';
import { UrlHelpers } from '../../../service/UrlHelpers';
import { LinkTarget, Tld } from '../../../typings/common';
import { DisclaimersApiResponse } from '../../common/requestsTypings';
import { CreativeType, ResourceType } from '../../common/typings';
import {
    CommonOptionGroupName,
    CommonOptionName,
    CreativeOptionAgeData,
    CreativeOptionColorPickerData,
    CreativeOptionDisclaimerData,
    CreativeOptionImageFileData,
    CreativeOptionsConfig,
    CreativeOptionSourceType,
    CreativeOptionTextData,
    CreativeOptionType,
    DisclaimerCountry,
    DisclaimerGroup
} from '../../CreativeOptions/typings';
import { getAddCreativeFileEditorId } from '../helpers';

import * as keyset from './CreativeConfigHelper.i18n';

const i18n = i18nFactory(keyset);
const i18nRaw = i18nRawFactory(keyset);

function getImageAdsHelpUrl(tld: Tld) {
    return UrlHelpers.getCommonHelpUrl('direct/required-docs-rules/ad-rules.xml#image-ads', tld);
}

function getAgeAlertsHelpUrl(tld: Tld) {
    return UrlHelpers.getCommonHelpUrl('direct/required-docs-rules/age-alerts.xml#image-ads', tld);
}

enum Limits {
    legal = 700,
    button = 17,
    special = 28,
    domain = 26,
    headline = 60,
    description = 75
}

function getColorOption(
    title: string,
    name: CommonOptionName,
    defaultValue: string,
    tooltip?: string | React.ReactNode
): CreativeOptionColorPickerData {
    return {
        editable: true,
        title,
        name,
        type: CreativeOptionType.ColorPicker,
        defaultValue,
        tooltip
    };
}

function getTextOption(title: string, limit?: number): CreativeOptionTextData {
    return {
        title,
        name: CommonOptionName.content,
        type: CreativeOptionType.Text,
        defaultValue: '',
        limit
    };
}

function getDisclaimerOption(
    title: string,
    groups: DisclaimerGroup[],
    items: DisclaimerCountry[]
): CreativeOptionDisclaimerData {
    return {
        title,
        name: CommonOptionName.content,
        type: CreativeOptionType.Disclaimer,
        items,
        groups
    };
}

function getAgeOption(
    title: string,
    name: CommonOptionName,
    allowedValues: string[],
    defaultValue: string
): CreativeOptionAgeData {
    return {
        title,
        name,
        type: CreativeOptionType.Age,
        allowedValues,
        defaultValue
    };
}

function getImageOption(data: {
    title: string;
    sources: CreativeOptionSourceType[];
    groupName: string;
    id: string;
    isCroppable: boolean;
    isSmartCenterable?: boolean;
}): CreativeOptionImageFileData {
    return {
        title: data.title,
        name: CommonOptionName.fileId,
        type: CreativeOptionType.File,
        resourceType: ResourceType.image,
        defaultValue: '',
        isCroppable: data.isCroppable,
        isSmartCenterable: data.isSmartCenterable,
        isMultisource: true,
        sources: data.sources,
        editorId: getAddCreativeFileEditorId(CreativeType.COMMON, data.id, data.groupName, CommonOptionName.fileId)
    };
}

export function getCommonConfig(id: string, disclaimers: DisclaimersApiResponse, tld: Tld): CreativeOptionsConfig {
    return {
        [CommonOptionGroupName.common]: {
            title: i18n('color-group-title'),
            options: [
                getColorOption(i18n('background-color-title'), CommonOptionName.backgroundColor, '#00FF80'),
                getColorOption(
                    i18n('border-color-title'),
                    CommonOptionName.borderColor,
                    '#00FF80',
                    i18nRaw('common-border-tooltip', {
                        link: (
                            <StyledLink
                                key="link"
                                theme={StyledLinkTheme.blue}
                                target={LinkTarget.blank}
                                href={getImageAdsHelpUrl(tld)}
                            >
                                {i18n('link-text')}
                            </StyledLink>
                        )
                    })
                )
            ]
        },
        [CommonOptionGroupName.logo]: {
            title: i18n('logo-group-title'),
            canBeHidden: true,
            options: [
                getImageOption({
                    title: i18n('logo-title'),
                    isCroppable: false,
                    sources: [
                        CreativeOptionSourceType.upload,
                        CreativeOptionSourceType.recent,
                        CreativeOptionSourceType.link,
                        CreativeOptionSourceType.idea
                    ],
                    id,
                    groupName: CommonOptionGroupName.logo
                })
            ]
        },
        [CommonOptionGroupName.image]: {
            title: i18n('image-group-title'),
            tooltip: i18n('image-group-tooltip'),
            isPrivate: true,
            options: [
                getImageOption({
                    title: i18n('image-title'),
                    isCroppable: true,
                    sources: [
                        CreativeOptionSourceType.crop,
                        CreativeOptionSourceType.upload,
                        CreativeOptionSourceType.stock,
                        CreativeOptionSourceType.recent,
                        CreativeOptionSourceType.link,
                        CreativeOptionSourceType.idea
                    ],
                    id,
                    groupName: CommonOptionGroupName.image
                })
            ]
        },
        [CommonOptionGroupName.button]: {
            title: i18n('button-group-title'),
            tooltip: i18nRaw('button-group-tooltip', {
                link: (
                    <StyledLink
                        key="link"
                        theme={StyledLinkTheme.blue}
                        target={LinkTarget.blank}
                        href={getImageAdsHelpUrl(tld)}
                    >
                        {i18n('link-text')}
                    </StyledLink>
                )
            }),
            options: [
                getColorOption(
                    i18n('background-color-title'),
                    CommonOptionName.backgroundColor,
                    '#000',
                    i18nRaw('button-background-color-tooltip', {
                        link: (
                            <StyledLink
                                key="link"
                                theme={StyledLinkTheme.blue}
                                target={LinkTarget.blank}
                                href={getImageAdsHelpUrl(tld)}
                            >
                                {i18n('link-text')}
                            </StyledLink>
                        )
                    })
                ),
                getColorOption(i18n('color-title'), CommonOptionName.color, '#000'),
                getTextOption(i18n('content-title'), Limits.button)
            ]
        },
        [CommonOptionGroupName.special]: {
            title: i18n('special-group-title'),
            canBeHidden: true,
            options: [
                getColorOption(i18n('background-color-title'), CommonOptionName.backgroundColor, '#000'),
                getColorOption(i18n('color-title'), CommonOptionName.color, '#000'),
                getTextOption(i18n('content-title'), Limits.special)
            ]
        },
        [CommonOptionGroupName.legal]: {
            title: i18n('legal-group-title'),
            tooltip: i18nRaw('legal-group-tooltip', {
                link: (
                    <StyledLink
                        key="link"
                        theme={StyledLinkTheme.blue}
                        target={LinkTarget.blank}
                        href={getAgeAlertsHelpUrl(tld)}
                    >
                        {i18n('link-text')}
                    </StyledLink>
                )
            }),
            canBeHidden: true,
            options: [
                getColorOption(i18n('color-title'), CommonOptionName.color, '#857b8c'),
                getColorOption(i18n('icon-color-title'), CommonOptionName.iconColor, '#857B8C'),
                getTextOption(i18n('content-title'), Limits.legal)
            ]
        },
        [CommonOptionGroupName.domain]: {
            title: i18n('domain-group-title'),
            canBeHidden: true,
            options: [
                getColorOption(i18n('color-title'), CommonOptionName.color, '#857B8C'),
                getTextOption(i18n('content-title'), Limits.domain)
            ]
        },
        [CommonOptionGroupName.headline]: {
            title: i18n('headline-group-title'),
            options: [
                getColorOption(i18n('color-title'), CommonOptionName.color, '#857B8C'),
                getTextOption(i18n('content-title'), Limits.headline)
            ]
        },
        [CommonOptionGroupName.description]: {
            title: i18n('description-group-title'),
            canBeHidden: true,
            options: [
                getColorOption(i18n('color-title'), CommonOptionName.color, '#857B8C'),
                getTextOption(i18n('content-title'), Limits.description)
            ]
        },
        [CommonOptionGroupName.disclaimer]: {
            title: i18n('disclaimer-group-title'),
            tooltip: i18nRaw('disclaimer-group-tooltip', {
                link: (
                    <StyledLink
                        key="link"
                        theme={StyledLinkTheme.blue}
                        target={LinkTarget.blank}
                        href={getAgeAlertsHelpUrl(tld)}
                    >
                        {i18n('link-text')}
                    </StyledLink>
                )
            }),
            canBeHidden: true,
            options: [
                getColorOption(i18n('color-title'), CommonOptionName.color, '#857B8C'),
                getDisclaimerOption(i18n('disclaimer-title'), disclaimers.groups, disclaimers.items)
            ]
        },
        [CommonOptionGroupName.ageRestriction]: {
            title: i18n('age-restriction-group-title'),
            tooltip: i18nRaw('age-restriction-group-tooltip', {
                link: (
                    <StyledLink
                        key="link"
                        theme={StyledLinkTheme.blue}
                        target={LinkTarget.blank}
                        href={getAgeAlertsHelpUrl(tld)}
                    >
                        {i18n('link-text')}
                    </StyledLink>
                )
            }),
            canBeHidden: true,
            options: [
                getColorOption(i18n('color-title'), CommonOptionName.color, '#857B8C'),
                getAgeOption(i18n('age-restriction-title'), CommonOptionName.value, ['0', '6', '12', '16', '18'], '18')
            ]
        },
        [CommonOptionGroupName.fade]: {
            title: i18n('fade-group-title'),
            tooltip: i18n('fade-group-tooltip'),
            canBeHidden: true,
            options: [getColorOption(i18n('fade-color-title'), CommonOptionName.color, '#AAAAAA')]
        },
        [CommonOptionGroupName.packshot]: {
            title: i18n('image-group-title'),
            tooltip: i18n('image-group-tooltip'),
            isPrivate: true,
            options: [
                getImageOption({
                    title: i18n('image-title'),
                    isCroppable: true,
                    sources: [
                        CreativeOptionSourceType.upload,
                        CreativeOptionSourceType.reset,
                        CreativeOptionSourceType.link
                    ],
                    id,
                    groupName: CommonOptionGroupName.packshot
                })
            ]
        }
    };
}

export function getAdaptiveConfig(id: string, disclaimers: DisclaimersApiResponse, tld: Tld): CreativeOptionsConfig {
    const config = getCommonConfig(id, disclaimers, tld);

    const imageConfig = config[CommonOptionGroupName.image];
    const imageOption = imageConfig.options[0];

    // проверка для тайпингов
    if (imageOption.type !== CreativeOptionType.File || imageOption.resourceType !== ResourceType.image) {
        return config;
    }

    imageOption.isCroppable = false;
    imageOption.isSmartCenterable = true;
    imageOption.sources.splice(0, 1, CreativeOptionSourceType.smartCenter);

    config[CommonOptionGroupName.domain].options = [getTextOption(i18n('content-title'), Limits.domain)];

    config[CommonOptionGroupName.disclaimer].options = [
        getDisclaimerOption(i18n('disclaimer-title'), disclaimers.groups, disclaimers.items)
    ];

    return config;
}
